import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, StatusBadge, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BriefcaseBusiness, Plus, Pencil } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  title: "", position_code: "", department_id: "", reporting_authority: "",
  min_qualification: "", preferred_qualification: "", min_experience: "", required_skills: "",
  required_training: "", required_competency: "", required_authorization: "", required_vaccination: "",
  police_verification_required: false, medical_fitness_required: false, probation_period: "", notice_period: "",
  salary_grade: "", salary_range: "", purpose: "", key_responsibilities: "",
  reporting_responsibilities: "", quality_responsibilities: "",
  safety_responsibilities: "", equipment_responsibilities: "", documentation_responsibilities: "", kpis: "",
  job_description: "", status: "Active",
};

function F({ label, k, form, set, area }) {
  return (
    <div><Label className="text-slate-600">{label}</Label>
      {area
        ? <Textarea value={form[k]} onChange={(e) => set({ ...form, [k]: e.target.value })} className="mt-1 focus-visible:ring-teal" data-testid={`desig-${k}`} />
        : <Input value={form[k]} onChange={(e) => set({ ...form, [k]: e.target.value })} className="mt-1 focus-visible:ring-teal" data-testid={`desig-${k}`} />}
    </div>
  );
}

export default function Designations() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const { data: depts } = useQuery({ queryKey: ["departments"], queryFn: async () => (await api.get("/departments")).data });
  const { data, isLoading } = useQuery({ queryKey: ["designations"], queryFn: async () => (await api.get("/designations")).data });

  const save = useMutation({
    mutationFn: async () => editId ? (await api.put(`/designations/${editId}`, form)).data : (await api.post("/designations", form)).data,
    onSuccess: () => { toast.success(editId ? "Designation updated" : "Designation created"); qc.invalidateQueries({ queryKey: ["designations"] }); setOpen(false); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (d) => { setForm({ ...EMPTY, ...d }); setEditId(d.id); setOpen(true); };

  return (
    <div>
      <PageHeader overline="Master Data" title="Designations & Job Descriptions" icon={BriefcaseBusiness}
        subtitle="Roles with qualifications, competency/authorization requirements, responsibilities and KPIs."
        actions={<Button onClick={openNew} className="bg-navy hover:bg-navy-700 btn-lift" data-testid="add-designation-btn"><Plus className="h-4 w-4 mr-1" /> Add Designation</Button>}
      />
      {isLoading ? <Loading /> : (data || []).length === 0 ? (
        <EmptyState icon={BriefcaseBusiness} title="No designations yet" hint="Create roles like Lab Technician, Phlebotomist, Consultant Pathologist."
          action={<Button onClick={openNew} className="bg-navy"><Plus className="h-4 w-4 mr-1" /> Add Designation</Button>} />
      ) : (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="overline text-slate-500">Code</TableHead>
              <TableHead className="overline text-slate-500">Title</TableHead>
              <TableHead className="overline text-slate-500">Department</TableHead>
              <TableHead className="overline text-slate-500">Police Verif.</TableHead>
              <TableHead className="overline text-slate-500">Status</TableHead>
              <TableHead className="text-right overline text-slate-500">Edit</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.map((d) => (
                <TableRow key={d.id} className="table-row-i" data-testid={`desig-row-${d.position_code}`}>
                  <TableCell className="font-mono-i text-sm text-teal">{d.position_code}</TableCell>
                  <TableCell className="font-medium text-navy">{d.title}</TableCell>
                  <TableCell className="text-slate-600">{d.department_name || "—"}</TableCell>
                  <TableCell>{d.police_verification_required ? <StatusBadge status="Pending" /> : <span className="text-slate-400 text-sm">Not required</span>}</TableCell>
                  <TableCell><StatusBadge status={d.status} /></TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => openEdit(d)} data-testid={`edit-desig-${d.position_code}`}><Pencil className="h-4 w-4" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="designation-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl text-navy">{editId ? "Edit" : "New"} Designation</DialogTitle></DialogHeader>
          <Tabs defaultValue="basic">
            <TabsList className="bg-slate-100">
              <TabsTrigger value="basic">Basics</TabsTrigger>
              <TabsTrigger value="reqs">Requirements</TabsTrigger>
              <TabsTrigger value="jd">Job Description</TabsTrigger>
            </TabsList>
            <TabsContent value="basic" className="grid sm:grid-cols-2 gap-4 mt-4">
              <F label="Title *" k="title" form={form} set={setForm} />
              <F label="Position Code *" k="position_code" form={form} set={setForm} />
              <div><Label className="text-slate-600">Department</Label>
                <Select value={form.department_id} onValueChange={(v) => setForm({ ...form, department_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="desig-dept"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white">{(depts || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <F label="Reporting Authority" k="reporting_authority" form={form} set={setForm} />
              <F label="Probation Period" k="probation_period" form={form} set={setForm} />
              <F label="Notice Period" k="notice_period" form={form} set={setForm} />
              <F label="Salary Grade" k="salary_grade" form={form} set={setForm} />
              <F label="Salary Range" k="salary_range" form={form} set={setForm} />
            </TabsContent>
            <TabsContent value="reqs" className="grid sm:grid-cols-2 gap-4 mt-4">
              <F label="Min Qualification" k="min_qualification" form={form} set={setForm} />
              <F label="Preferred Qualification" k="preferred_qualification" form={form} set={setForm} />
              <F label="Min Experience" k="min_experience" form={form} set={setForm} />
              <F label="Required Skills" k="required_skills" form={form} set={setForm} area />
              <F label="Required Training" k="required_training" form={form} set={setForm} area />
              <F label="Required Competency" k="required_competency" form={form} set={setForm} area />
              <F label="Required Authorization" k="required_authorization" form={form} set={setForm} area />
              <F label="Required Vaccination" k="required_vaccination" form={form} set={setForm} area />
              <div className="flex items-center justify-between rounded-md border border-slate-200 px-4 py-3">
                <span className="text-sm text-slate-700">Police Verification Required</span>
                <Switch checked={form.police_verification_required} onCheckedChange={(v) => setForm({ ...form, police_verification_required: v })} data-testid="desig-police" />
              </div>
              <div className="flex items-center justify-between rounded-md border border-slate-200 px-4 py-3">
                <span className="text-sm text-slate-700">Medical Fitness Required</span>
                <Switch checked={form.medical_fitness_required} onCheckedChange={(v) => setForm({ ...form, medical_fitness_required: v })} data-testid="desig-medical" />
              </div>
            </TabsContent>
            <TabsContent value="jd" className="grid gap-4 mt-4">
              <F label="Purpose" k="purpose" form={form} set={setForm} area />
              <F label="Key Responsibilities" k="key_responsibilities" form={form} set={setForm} area />
              <div className="grid sm:grid-cols-2 gap-4">
                <F label="Quality Responsibilities" k="quality_responsibilities" form={form} set={setForm} area />
                <F label="Safety Responsibilities" k="safety_responsibilities" form={form} set={setForm} area />
              </div>
              <F label="KPIs" k="kpis" form={form} set={setForm} area />
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={!form.title || !form.position_code || save.isPending} onClick={() => save.mutate()} data-testid="save-designation">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
